import React, { useState, useEffect, useCallback, useMemo } from "react";
import Datetime from "react-datetime";
import "react-datetime/css/react-datetime.css";
import { MdWindow } from "react-icons/md";
import { IoMdAdd } from "react-icons/io";
import { FaTooth, FaCopy } from "react-icons/fa";
import Button from "react-bootstrap/Button";
import Dropdown from "react-bootstrap/Dropdown";
import InputGroup from "react-bootstrap/InputGroup";
import Form from "react-bootstrap/Form";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Modal from "react-bootstrap/Modal";
import ListGroup from "react-bootstrap/ListGroup";
import Enclosure from "./enclosures";
import "./WorkRequested.css"; // Import the CSS file

// Procedure Selector Modal Component
const ProcedureSelectorModal = ({
  show,
  handleClose,
  onSelect,
  allProcedures,
  procedureIndex,
}) => {
  const [searchTerm, setSearchTerm] = useState("");

  const frequentlyUsedProcedureNames = [
    "1-7 ALIGNERS (DUAL ARCH)",
    "3D PRINTED MODELS",
  ];

  const filteredProcedures = useMemo(() => {
    if (!searchTerm) {
      return allProcedures;
    }
    return allProcedures.filter((proc) =>
      proc.label.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [searchTerm, allProcedures]);

  const handleProcedureClick = (procedureId) => {
    onSelect(procedureId, procedureIndex);
    handleClose();
  };

  const frequentlyUsed = filteredProcedures.filter((p) =>
    frequentlyUsedProcedureNames.includes(p.label)
  );

  const generalProcedures = filteredProcedures.filter(
    (p) => !frequentlyUsedProcedureNames.includes(p.label) && p.value
  );

  return (
    <Modal
      show={show}
      onHide={handleClose}
      size="lg"
      centered
      dialogClassName="procedure-modal"
    >
      <Modal.Header closeButton className="modal-header-custom">
        <Modal.Title className="modal-title-custom">Select Procedure</Modal.Title>
      </Modal.Header>
      <Modal.Body className="modal-body-custom">
        <div style={{ padding: "clamp(8px, 0.8vw, 16px)", borderBottom: "1px solid #dee2e6" }}>
          <Form.Control
            type="text"
            placeholder="Search"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{fontSize: "clamp(12px, 0.9vw, 16px)"}}
          />
        </div>
        <div style={{ maxHeight: "60vh", overflowY: "auto", padding: "clamp(8px, 0.8vw, 16px)" }}>
          {frequentlyUsed.length > 0 && (
            <>
              <h5 style={{fontSize: "clamp(13px, 1vw, 18px)"}}>Frequently Used</h5>
              <ListGroup variant="flush">
                {frequentlyUsed.map((proc, idx) => (
                  <ListGroup.Item
                    key={`freq-${proc.value}`}
                    action
                    onClick={() => handleProcedureClick(proc.value)}
                    style={{
                      cursor: "pointer",
                      border: "none",
                      paddingLeft: "0",
                      fontSize: "clamp(12px, 0.9vw, 16px)",
                    }}
                  >
                    {proc.label}
                  </ListGroup.Item>
                ))}
              </ListGroup>
              <hr />
            </>
          )}

          {generalProcedures.length > 0 && (
            <>
              <h5 style={{fontSize: "clamp(13px, 1vw, 18px)"}}>General Procedures</h5>
              <ListGroup variant="flush">
                {generalProcedures.map((proc, idx) => (
                  <ListGroup.Item
                    key={`gen-${proc.value}`}
                    action
                    onClick={() => handleProcedureClick(proc.value)}
                    style={{
                      cursor: "pointer",
                      border: "none",
                      paddingLeft: "0",
                      fontSize: "clamp(12px, 0.9vw, 16px)",
                    }}
                  >
                    {proc.label}
                  </ListGroup.Item>
                ))}
              </ListGroup>
            </>
          )}

          {frequentlyUsed.length === 0 && generalProcedures.length === 0 && (
            <p style={{fontSize: "clamp(12px, 0.9vw, 16px)"}}>No procedures available for this account.</p>
          )}
        </div>
      </Modal.Body>
    </Modal>
  );
};

// Tooth Selector Modal Component
const ToothSelectorModal = ({
  show,
  handleClose,
  currentTeeth,
  onSave,
  procedureIndex,
}) => {
  const [selectedTeeth, setSelectedTeeth] = useState([]);

  useEffect(() => {
    if (show) {
      setSelectedTeeth(
        currentTeeth
          ? currentTeeth
              .split(",")
              .map((t) => t.trim())
              .filter((t) => t)
          : []
      );
    }
  }, [show, currentTeeth]);

  const toggleTooth = (toothNumber) => {
    setSelectedTeeth((prevSelected) =>
      prevSelected.includes(toothNumber.toString())
        ? prevSelected.filter((t) => t !== toothNumber.toString())
        : [...prevSelected, toothNumber.toString()]
    );
  };

  const toggleGroup = (teethArray) => {
    const teeth = teethArray.map((tooth) => tooth.toString());
    setSelectedTeeth((prevSelected) => {
      const allSelected = teeth.every((tooth) => prevSelected.includes(tooth));
      if (allSelected) {
        return prevSelected.filter((t) => !teeth.includes(t));
      } else {
        const newSelection = [...prevSelected];
        teeth.forEach((tooth) => {
          if (!newSelection.includes(tooth)) {
            newSelection.push(tooth);
          }
        });
        return newSelection;
      }
    });
  };

  const handleDone = () => {
    onSave(
      selectedTeeth.sort((a, b) => parseInt(a) - parseInt(b)).join(", "),
      procedureIndex
    );
    handleClose();
  };

  const renderToothButton = (toothNumber) => (
    <button
      key={toothNumber}
      onClick={() => toggleTooth(toothNumber)}
      className={selectedTeeth.includes(toothNumber.toString()) ? "tooth-button-selected" : "tooth-button"}
    >
      {toothNumber}
    </button>
  );

  const teethLayout = {
    upperRight: [18, 17, 16, 15, 14, 13, 12, 11],
    upperLeft: [21, 22, 23, 24, 25, 26, 27, 28],
    lowerRight: [48, 47, 46, 45, 44, 43, 42, 41],
    lowerLeft: [31, 32, 33, 34, 35, 36, 37, 38],
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      size="lg"
      centered
      dialogClassName="tooth-modal"
      style={{ zIndex: 1050 }}
    >
      <Modal.Header closeButton className="modal-header-custom">
        <Modal.Title className="modal-title-custom">Tooth Selector</Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ padding: "clamp(10px, 1.5vw, 20px)" }}>
        <div className="text-center mb-3">
          <h5
            className="arch-heading"
            onClick={() =>
              toggleGroup([...teethLayout.upperRight, ...teethLayout.upperLeft])
            }
          >
            Upper Arch
          </h5>
        </div>
        <div className="mb-4">
          <table className="tooth-table">
            <thead>
              <tr>
                <th
                  colSpan="8"
                  onClick={() => toggleGroup(teethLayout.upperRight)}
                >
                  Upper Right
                </th>
                <th
                  colSpan="8"
                  onClick={() => toggleGroup(teethLayout.upperLeft)}
                >
                  Upper Left
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                {teethLayout.upperRight.map((tooth) => (
                  <td key={tooth}>
                    {renderToothButton(tooth)}
                  </td>
                ))}
                {teethLayout.upperLeft.map((tooth) => (
                  <td key={tooth}>
                    {renderToothButton(tooth)}
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
        <hr />
        <div className="text-center mb-3">
          <h5
            className="arch-heading"
            onClick={() =>
              toggleGroup([...teethLayout.lowerRight, ...teethLayout.lowerLeft])
            }
          >
            Lower Arch
          </h5>
        </div>
        <div>
          <table className="tooth-table">
            <thead>
              <tr>
                <th
                  colSpan="8"
                  onClick={() => toggleGroup(teethLayout.lowerRight)}
                >
                  Lower Right
                </th>
                <th
                  colSpan="8"
                  onClick={() => toggleGroup(teethLayout.lowerLeft)}
                >
                  Lower Left
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                {teethLayout.lowerRight.map((tooth) => (
                  <td key={tooth}>
                    {renderToothButton(tooth)}
                  </td>
                ))}
                {teethLayout.lowerLeft.map((tooth) => (
                  <td key={tooth}>
                    {renderToothButton(tooth)}
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose} style={{fontSize: "clamp(12px, 0.9vw, 16px)"}}>
          Cancel
        </Button>
        <Button
          onClick={handleDone}
          className="input-group-button"
          style={{
            fontSize: "clamp(12px, 0.9vw, 16px)",
          }}
        >
          Done
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

const WorkRequested = ({ onFormSubmit, account, formErrors }) => {
  const [start_date, setStartDate] = useState(new Date());
  const [delivery_date, setDeliveryDate] = useState(new Date());
  const [arrive_date, setArrivedDate] = useState(new Date());
  const [color, setColor] = useState({ name: "", hex: "#ffffff" });
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [pan_number, setPanNumber] = useState("");
  const [selectedValues, setSelectedValues] = useState([]);
  const [procedures, setProcedures] = useState([
    {
      procedureName: "",
      procedureId: "",
      teethCount: "",
      shade: "",
      alley: "",
      contacts: "",
      occlusion: "",
      instructionRoom: "",
      retention: "",
      margin: "",
      emergence: "",
      price: 0,
      key: Date.now(),
    },
  ]);
  const [selectedComponent, setSelectedComponent] = useState("");
  const [count, setCount] = useState("");
  const [procedureOptions, setProcedureOptions] = useState([]);
  const [isLoadingProcedures, setIsLoadingProcedures] = useState(true);
  const [showToothSelectorModal, setShowToothSelectorModal] = useState(false);
  const [currentProcedureIndex, setCurrentProcedureIndex] = useState(null);
  const [showProcedureSelectorModal, setShowProcedureSelectorModal] = useState(false);
  const [labId, setLabId] = useState(null);
  const [userType, setUserType] = useState('lab');

  const alloyOptions = [
    "Select Alloy / Material",
    "Gold",
    "Porcelain",
    "Zirconia",
    "PFM",
  ];

  const contactOptions = [
    "Select Contacts / Embrasures",
    "Tight",
    "Normal",
    "Open",
  ];

  const occlusalOptions = [
    "Select Occlusal Contact",
    "Heavy",
    "Normal",
    "Light",
  ];

  const roomOptions = ["Select Insufficient Room", "Yes", "No"];

  const retentionOptions = [
    "Select Retention",
    "Excellent",
    "Good",
    "Fair",
    "Poor",
  ];

  const marginOptions = [
    "Select Margin Position",
    "Supragingival",
    "Equigingival",
    "Subgingival",
  ];

  const panColors = [
    { name: "White", hex: "#FFFFFF" },
    { name: "Purple", hex: "#800080" },
    { name: "megenta", hex: "#FF00FF" },
    { name: "Red", hex: "#FF0000" },
    { name: "Maroon", hex: "#800000" },
    { name: "Brown", hex: "#A52A2A" },
    { name: "Orange", hex: "#FFA500" },
    { name: "Yellow", hex: "#FFFF00" },
    { name: "Lime", hex: "#00FF00" },
    { name: "Green", hex: "#008000" },
    { name: "Teal", hex: "#808000" },
    { name: "Cyan", hex: "#008080" },
    { name: "Blue", hex: "#0000FF" },
    { name: "Navy", hex: "#000080" },
    { name: "Silver", hex: "#C0C0C0" },
    { name: "Gray", hex: "#808080" },
    { name: "Black", hex: "#000000" },
  ];

  // Function to get labId and userType from localStorage
  const getUserInfo = useCallback(() => {
    try {
      const storedLabId = localStorage.getItem('userLabId');
      const storedUserType = localStorage.getItem('userType');

      console.log('WorkRequested - Loaded from localStorage:');
      console.log('LabId:', storedLabId);
      console.log('UserType:', storedUserType);

      if (storedLabId) {
        setLabId(storedLabId);
      }

      if (storedUserType) {
        setUserType(storedUserType);
      } else if (storedLabId) {
        setUserType('lab'); // Default to lab if labId exists
      } else {
        setUserType('admin'); // If no labId, assume admin
      }
    } catch (error) {
      console.error('Error getting user info:', error);
    }
  }, []);

  // Get user info on component mount
  useEffect(() => {
    getUserInfo();
  }, [getUserInfo]);

  // Fetch procedures based on labId
  useEffect(() => {
    const fetchProcedures = async () => {
      if (!account) {
        setProcedureOptions([]);
        setIsLoadingProcedures(false);
        return;
      }

      console.log('Fetching procedures for:', { account, labId, userType });
      setIsLoadingProcedures(true);

      try {
        let proceduresData = [];

        if (userType === 'lab' && labId) {
          // For lab users: fetch lab-specific procedures
          console.log(`📡 Fetching lab procedures for labId: ${labId}`);

          try {
            const response = await fetch(
              `https://hms.backend.hiragroupcloud.com/api/procedures/lab-procedures/${labId}`,
              {
                headers: {
                  'Accept': 'application/json',
                  'Content-Type': 'application/json',
                }
              }
            );

            if (!response.ok) {
              throw new Error(`Lab procedures API error: ${response.status}`);
            }

            const result = await response.json();
            console.log('Lab procedures response:', result);

            if (result.success && result.data) {
              proceduresData = result.data;
              console.log(`📈 Found ${proceduresData.length} lab procedures`);
            } else {
              console.warn('No procedures found for lab, trying all procedures');
              // Fallback to all procedures
              const allProceduresResponse = await fetch(
                "https://hms.backend.hiragroupcloud.com/api/procedures/procedures"
              );
              const allResult = await allProceduresResponse.json();
              if (allResult.success && allResult.data) {
                proceduresData = allResult.data;
                console.log(`📈 Fallback to ${proceduresData.length} all procedures`);
              }
            }
          } catch (labError) {
            console.error('Error fetching lab procedures, trying all procedures:', labError);
            // Fallback to all procedures
            try {
              const allProceduresResponse = await fetch(
                "https://hms.backend.hiragroupcloud.com/api/procedures/procedures"
              );
              const allResult = await allProceduresResponse.json();
              if (allResult.success && allResult.data) {
                proceduresData = allResult.data;
                console.log(`📈 Fallback to ${proceduresData.length} all procedures after error`);
              }
            } catch (fallbackError) {
              console.error('Error fetching all procedures:', fallbackError);
            }
          }
        } else {
          // For admin users or if no labId: fetch all procedures
          console.log('📡 Fetching all procedures (admin mode)');

          try {
            const response = await fetch(
              "https://hms.backend.hiragroupcloud.com/api/procedures/procedures"
            );
            const result = await response.json();
            console.log('All procedures response:', result);

            if (result.success && result.data) {
              proceduresData = result.data;
              console.log(`📈 Found ${proceduresData.length} all procedures`);
            }
          } catch (adminError) {
            console.error('Error fetching all procedures:', adminError);
          }
        }

        // Transform to Select options format
        const options = proceduresData
          .filter(proc => proc && (proc.procedure_name || proc.name))
          .map(proc => ({
            value: proc._id || proc.procedure_code,
            label: `${proc.procedure_name || proc.name} (${proc.procedure_code || 'N/A'})`,
            procedure_name: proc.procedure_name || proc.name,
            procedure_code: proc.procedure_code,
            price: proc.price || 0,
            unitCount: proc.unitCount || 'Count by Teeth'
          }));

        if (options.length === 0) {
          setProcedureOptions([{
            label: 'No procedures found for your lab. Please contact admin.',
            value: '',
            price: 0,
            unitCount: 'Count by Teeth'
          }]);
        } else {
          setProcedureOptions([
            { label: 'Select a procedure...', value: '', price: 0, unitCount: 'Count by Teeth' },
            ...options,
          ]);
        }
      } catch (error) {
        console.error('Error in fetchProcedures:', error);
        setProcedureOptions([{
          label: 'Error loading procedures. Please try again.',
          value: '',
          price: 0,
          unitCount: 'Count by Teeth'
        }]);
      } finally {
        setIsLoadingProcedures(false);
      }
    };

    fetchProcedures();
  }, [account, labId, userType]);

  const updateFormData = useCallback(() => {
    const formData = {
      start_date:
        start_date instanceof Date && !isNaN(start_date) ? start_date : null,
      arrive_date:
        arrive_date instanceof Date && !isNaN(arrive_date) ? arrive_date : null,
      delivery_date:
        delivery_date instanceof Date && !isNaN(delivery_date)
          ? delivery_date
          : null,
      color,
      pan_number,
      components: selectedComponent ? { [selectedComponent]: count } : null,
      selectedValues,
      procedures: procedures
        .filter(p => p.procedureId && p.teethCount && p.shade)
        .map(p => ({
          procedureId: p.procedureId,
          procedure: p.procedureName,
          teeth: p.teethCount,
          shade: p.shade,
          alloyMaterial: p.alley || '',
          contactsEmbrasures: p.contacts || '',
          occlusalContact: p.occlusion || '',
          insufficientRoom: p.instructionRoom || '',
          retention: p.retention || '',
          marginPosition: p.margin || '',
          emergenceWidth: p.emergence || '',
          basePrice: Number(p.basePrice) || 0,
          unitCount: p.unitCount || 'Count by Teeth',
        })),
    };
    if (onFormSubmit) onFormSubmit(formData);
  }, [
    start_date,
    arrive_date,
    delivery_date,
    color,
    pan_number,
    selectedComponent,
    count,
    selectedValues,
    procedures,
    onFormSubmit,
  ]);

  useEffect(() => {
    updateFormData();
  }, [
    start_date,
    arrive_date,
    color,
    procedures,
    pan_number,
    delivery_date,
    selectedComponent,
    count,
    selectedValues,
  ]);

  const toggleColorPicker = () => {
    setShowColorPicker(!showColorPicker);
  };

  const handleColorSelect = (selectedColor) => {
    setColor(selectedColor);
    setShowColorPicker(false);
  };

  const handleSelectChange = (e) => {
    setSelectedComponent(e.target.value);
  };

  const addProcedure = () => {
    setProcedures([
      ...procedures,
      {
        procedureName: "",
        procedureId: "",
        teethCount: "",
        shade: "",
        alley: "",
        contacts: "",
        occlusion: "",
        instructionRoom: "",
        retention: "",
        margin: "",
        emergence: "",
        price: 0,
        key: Date.now(),
      },
    ]);
  };

  const removeProcedure = (key) => {
    setProcedures(procedures.filter((proc) => proc.key !== key));
  };

  const handleProcedureSelect = (procedureId, index) => {
    const selected = procedureOptions.find(p => p.value === procedureId);
    if (!selected) return;
    const updated = [...procedures];
    updated[index] = {
      ...updated[index],
      procedureName: selected.label.replace(/ \(ID:.*\)\s*-\s*\$\d+(\.\d+)?$/, '').trim(),
      procedureId: selected.value,
      basePrice: selected.price,
      unitCount: selected.unitCount,
      price: 0,
    };
    setProcedures(updated);
  };

  const handleSaveTeeth = (selectedTeethValue, index) => {
    const updatedProcedures = [...procedures];
    updatedProcedures[index].teethCount = selectedTeethValue;
    setProcedures(updatedProcedures);
    setShowToothSelectorModal(false);
  };

  const openToothSelector = (index) => {
    setCurrentProcedureIndex(index);
    setShowToothSelectorModal(true);
  };

  const openProcedureSelector = (index) => {
    setCurrentProcedureIndex(index);
    setShowProcedureSelectorModal(true);
  };

  function getContrastColor(hexColor) {
    if (!hexColor) return "#000000";
    const r = parseInt(hexColor.substr(1, 2), 16);
    const g = parseInt(hexColor.substr(3, 2), 16);
    const b = parseInt(hexColor.substr(5, 2), 16);
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance > 0.5 ? "#000000" : "#ffffff";
  }

  return (
    <>
      <h3 className="work-requested-heading">
        <MdWindow className="work-requested-heading-icon" />{" "}
        Work Requested
      </h3>
      <div className="work-requested-container">
        {/* Show error if procedures couldn't be loaded */}
        {procedureOptions.length === 1 && procedureOptions[0].value === '' && (
          <div className="error-message">
            <strong>Error:</strong> {procedureOptions[0].label}
          </div>
        )}

        <div className="form-container">
          <div className="row">
            <div className="col-6">
              <div className="form-group-style">
                <label className="form-label-style">Account Send Date</label>
                <Datetime
                  value={start_date}
                  onChange={(date) => {
                    if (date && date.isValid && date.isValid()) {
                      setStartDate(date.toDate());
                    } else {
                      setStartDate(null);
                    }
                  }}
                  input={true}
                  inputProps={{
                    className: "form-input-style",
                    style: {
                      width: "100%",
                    }
                  }}
                />
              </div>
              <div className="form-group-style">
                <label className="form-label-style">Select Color</label>
                <div className="color-picker-container">
                  <input
                    type="text"
                    className="color-input"
                    value={color.name || ""}
                    onClick={toggleColorPicker}
                    readOnly
                    style={{
                      backgroundColor: color.hex,
                      color: getContrastColor(color.hex),
                    }}
                  />
                  {showColorPicker && (
                    <div className="color-picker-popup">
                      {panColors.map((col, idx) => (
                        <div
                          key={idx}
                          className="color-swatch"
                          style={{ background: col.hex }}
                          onClick={() => handleColorSelect(col)}
                        />
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="col-6">
              <div className="form-group-style">
                <label className="form-label-style">Arrived</label>
                <Datetime
                  value={arrive_date}
                  onChange={(date) => {
                    if (date && date.isValid && date.isValid()) {
                      setArrivedDate(date.toDate());
                    } else {
                      setArrivedDate(null);
                    }
                  }}
                  input={true}
                  inputProps={{
                    className: "form-input-style",
                    style: {
                      width: "100%",
                    }
                  }}
                />
              </div>
              <div className="form-group-style">
                <label className="form-label-style">Pan Number</label>
                <input
                  type="text"
                  value={pan_number}
                  onChange={(e) => setPanNumber(e.target.value)}
                  className="form-input-style"
                  name="pan_number"
                  placeholder="Enter Pan Number"
                />
              </div>
            </div>
          </div>
          <div className="row" style={{ marginTop: "10px" }}>
            <div className="col-6">
              <div className="form-group-style">
                <label className="form-label-style">Enclosures</label>
                <div className="enclosure-wrapper">
                  <Enclosure onSelectionChange={setSelectedValues} />
                </div>
              </div>
              {procedures.map((procedure, index) => (
                <div key={procedure.key} className="procedure-section-style">
                  <Form.Group
                    as={Row}
                    className="mb-3"
                    controlId={`formProcedure-${index}`}
                  >
                    <Form.Label className="required-label-style">
                      Procedure <span style={{ color: "red" }}>*</span>
                    </Form.Label>
                    <InputGroup style={{ width: "120%" }}>
                      <Form.Select
                        value={procedure.procedureId}
                        onChange={(e) => handleProcedureSelect(e.target.value, index)}
                        disabled={isLoadingProcedures}
                        style={{
                          cursor: "pointer",
                          backgroundColor: "#fff",
                          fontSize: "clamp(11px, 0.8vw, 14px)",
                        }}
                      >
                        {isLoadingProcedures ? (
                          <option>Loading procedures...</option>
                        ) : procedureOptions.length === 1 && procedureOptions[0].value === '' ? (
                          // Show the error message option
                          <option value="">{procedureOptions[0].label}</option>
                        ) : (
                          <>
                            <option value="">Select a procedure...</option>
                            {procedureOptions
                              .filter(opt => opt.value !== '') // Filter out the error message option
                              .map((option, idx) => (
                                <option key={idx} value={option.value}>
                                  {option.label}
                                </option>
                              ))
                            }
                          </>
                        )}
                      </Form.Select>
                      <Button
                        variant="outline-secondary"
                        onClick={() => openProcedureSelector(index)}
                        className="input-group-button"
                        disabled={isLoadingProcedures || (procedureOptions.length === 1 && procedureOptions[0].value === '')}
                      >
                        <FaCopy style={{ color: "#fff" }} />
                      </Button>
                    </InputGroup>
                  </Form.Group>
                  {procedure.procedureId && (
                    <>
                      <Form.Group
                        as={Row}
                        className="mb-3"
                        controlId={`formTeeth-${index}`}
                      >
                        <Form.Label column sm={2} style={{fontSize:"clamp(11px, 0.8vw, 15px)"}}>
                          Teeth <span style={{ color: "red" }}>*</span>
                        </Form.Label>
                        <Col sm={10}>
                          <InputGroup>
                            <Form.Control
                              type="text"
                              placeholder="Select teeth"
                              value={procedure.teethCount}
                              readOnly
                              onClick={() => openToothSelector(index)}
                              style={{
                                cursor: "pointer",
                                fontSize: "clamp(11px, 0.8vw, 14px)",
                              }}
                            />
                            <Button
                              variant="outline-secondary"
                              onClick={() => openToothSelector(index)}
                              className="input-group-button"
                            >
                              <FaTooth style={{ color: "#fff" }} />
                            </Button>
                          </InputGroup>
                        </Col>
                      </Form.Group>
                      <Form.Group
                        as={Row}
                        className="mb-3"
                        controlId={`formShade-${index}`}
                      >
                        <Form.Label column sm={2} style={{fontSize:"clamp(11px, 0.8vw, 15px)"}}>
                          Shade <span style={{ color: "red" }}>*</span>
                        </Form.Label>
                        <Col sm={10}>
                          <Form.Control
                            type="text"
                            value={procedure.shade}
                            onChange={(e) => {
                              const updatedProcedures = [...procedures];
                              updatedProcedures[index].shade = e.target.value;
                              setProcedures(updatedProcedures);
                            }}
                            placeholder="Enter shade"
                            style={{fontSize: "clamp(11px, 0.8vw, 14px)"}}
                          />
                        </Col>
                      </Form.Group>
                      <Form.Group
                        as={Row}
                        className="mb-3"
                        controlId={`formAlloyMaterial-${index}`}
                      >
                        <Form.Label column sm={2} style={{fontSize:"clamp(11px, 0.8vw, 14px)"}}>
                          Alloy / Material
                        </Form.Label>
                        <Col sm={10}>
                          <Dropdown
                            onSelect={(eventKey) => {
                              const updatedProcedures = [...procedures];
                              updatedProcedures[index].alley = eventKey;
                              setProcedures(updatedProcedures);
                            }}
                          >
                            <Dropdown.Toggle
                              variant="outline-secondary"
                              id={`dropdown-alloy-${index}`}
                              className="dropdown-toggle-custom"
                            >
                              <span>{procedure.alley || "Select Alloy / Material"}</span>
                            </Dropdown.Toggle>
                            <Dropdown.Menu className="dropdown-menu-custom">
                              {alloyOptions.map((opt) => (
                                <Dropdown.Item
                                  key={opt}
                                  eventKey={opt}
                                  className="dropdown-item-custom"
                                >
                                  {opt}
                                </Dropdown.Item>
                              ))}
                            </Dropdown.Menu>
                          </Dropdown>
                        </Col>
                      </Form.Group>
                      <Form.Group
                        as={Row}
                        className="mb-3"
                        controlId={`formContacts-${index}`}
                      >
                        <Form.Label column sm={2} style={{fontSize:"clamp(11px, 0.8vw, 14px)"}}>
                          Contacts / Embrasures
                        </Form.Label>
                        <Col sm={10}>
                          <Dropdown
                            onSelect={(eventKey) => {
                              const updatedProcedures = [...procedures];
                              updatedProcedures[index].contacts = eventKey;
                              setProcedures(updatedProcedures);
                            }}
                          >
                            <Dropdown.Toggle
                              variant="outline-secondary"
                              id={`dropdown-contacts-${index}`}
                              className="dropdown-toggle-custom"
                            >
                              <span>{procedure.contacts || "Select Contacts / Embrasures"}</span>
                            </Dropdown.Toggle>
                            <Dropdown.Menu className="dropdown-menu-custom">
                              {contactOptions.map((opt) => (
                                <Dropdown.Item
                                  key={opt}
                                  eventKey={opt}
                                  className="dropdown-item-custom"
                                >
                                  {opt}
                                </Dropdown.Item>
                              ))}
                            </Dropdown.Menu>
                          </Dropdown>
                        </Col>
                      </Form.Group>
                      <Form.Group
                        as={Row}
                        className="mb-3"
                        controlId={`formOcclusal-${index}`}
                      >
                        <Form.Label column sm={2} style={{fontSize:"clamp(11px, 0.8vw, 14px)"}}>
                          Occlusal Contact
                        </Form.Label>
                        <Col sm={10}>
                          <Dropdown
                            onSelect={(eventKey) => {
                              const updatedProcedures = [...procedures];
                              updatedProcedures[index].occlusion = eventKey;
                              setProcedures(updatedProcedures);
                            }}
                          >
                            <Dropdown.Toggle
                              variant="outline-secondary"
                              id={`dropdown-occlusal-${index}`}
                              className="dropdown-toggle-custom"
                            >
                              <span>{procedure.occlusion || "Select Occlusal Contact"}</span>
                            </Dropdown.Toggle>
                            <Dropdown.Menu className="dropdown-menu-custom">
                              {occlusalOptions.map((opt) => (
                                <Dropdown.Item
                                  key={opt}
                                  eventKey={opt}
                                  className="dropdown-item-custom"
                                >
                                  {opt}
                                </Dropdown.Item>
                              ))}
                            </Dropdown.Menu>
                          </Dropdown>
                        </Col>
                      </Form.Group>
                      <Form.Group
                        as={Row}
                        className="mb-3"
                        controlId={`formRoom-${index}`}
                      >
                        <Form.Label column sm={2} style={{fontSize:"clamp(11px, 0.8vw, 14px)"}}>
                          Insufficient Room
                        </Form.Label>
                        <Col sm={10}>
                          <Dropdown
                            onSelect={(eventKey) => {
                              const updatedProcedures = [...procedures];
                              updatedProcedures[index].instructionRoom = eventKey;
                              setProcedures(updatedProcedures);
                            }}
                          >
                            <Dropdown.Toggle
                              variant="outline-secondary"
                              id={`dropdown-room-${index}`}
                              className="dropdown-toggle-custom"
                            >
                              <span>{procedure.instructionRoom || "Select Insufficient Room"}</span>
                            </Dropdown.Toggle>
                            <Dropdown.Menu className="dropdown-menu-custom">
                              {roomOptions.map((opt) => (
                                <Dropdown.Item
                                  key={opt}
                                  eventKey={opt}
                                  className="dropdown-item-custom"
                                >
                                  {opt}
                                </Dropdown.Item>
                              ))}
                            </Dropdown.Menu>
                          </Dropdown>
                        </Col>
                      </Form.Group>
                      <Form.Group
                        as={Row}
                        className="mb-3"
                        controlId={`formRetention-${index}`}
                      >
                        <Form.Label column sm={2} style={{fontSize:"clamp(11px, 0.8vw, 14px)"}}>
                          Retention
                        </Form.Label>
                        <Col sm={10}>
                          <Dropdown
                            onSelect={(eventKey) => {
                              const updatedProcedures = [...procedures];
                              updatedProcedures[index].retention = eventKey;
                              setProcedures(updatedProcedures);
                            }}
                          >
                            <Dropdown.Toggle
                              variant="outline-secondary"
                              id={`dropdown-retention-${index}`}
                              className="dropdown-toggle-custom"
                            >
                              <span>{procedure.retention || "Select Retention"}</span>
                            </Dropdown.Toggle>
                            <Dropdown.Menu className="dropdown-menu-custom">
                              {retentionOptions.map((opt) => (
                                <Dropdown.Item
                                  key={opt}
                                  eventKey={opt}
                                  className="dropdown-item-custom"
                                >
                                  {opt}
                                </Dropdown.Item>
                              ))}
                            </Dropdown.Menu>
                          </Dropdown>
                        </Col>
                      </Form.Group>
                      <Form.Group
                        as={Row}
                        className="mb-3"
                        controlId={`formMargin-${index}`}
                      >
                        <Form.Label column sm={2} style={{fontSize:"clamp(11px, 0.8vw, 15px)"}}>
                          Margin Position
                        </Form.Label>
                        <Col sm={10}>
                          <Dropdown
                            onSelect={(eventKey) => {
                              const updatedProcedures = [...procedures];
                              updatedProcedures[index].margin = eventKey;
                              setProcedures(updatedProcedures);
                            }}
                          >
                            <Dropdown.Toggle
                              variant="outline-secondary"
                              id={`dropdown-margin-${index}`}
                              className="dropdown-toggle-custom"
                            >
                              <span>{procedure.margin || "Select Margin Position"}</span>
                            </Dropdown.Toggle>
                            <Dropdown.Menu className="dropdown-menu-custom">
                              {marginOptions.map((opt) => (
                                <Dropdown.Item
                                  key={opt}
                                  eventKey={opt}
                                  className="dropdown-item-custom"
                                >
                                  {opt}
                                </Dropdown.Item>
                              ))}
                            </Dropdown.Menu>
                          </Dropdown>
                        </Col>
                      </Form.Group>
                    </>
                  )}
                  {procedures.length > 1 && (
                    <button
                      className="remove-procedure-button"
                      onClick={() => removeProcedure(procedure.key)}
                    >
                      Remove Procedure
                    </button>
                  )}
                </div>
              ))}
            </div>
            <div className="col-6">
              <div className="form-group-style">
                <label className="form-label-style">Components</label>
                <div className="component-selector">
                  <select
                    className="component-select"
                    value={selectedComponent}
                    onChange={handleSelectChange}
                  >
                    <option value="">Select a component</option>
                    <option value="gumWorkComposite">GUM WORK COMPOSITE</option>
                  </select>
                </div>
              </div>
              {selectedComponent && (
                <div
                  style={{
                    marginTop: "clamp(5px, 0.5vw, 10px)",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <label style={{fontSize: "clamp(11px, 0.8vw, 14px)"}}>GUM WORK COMPOSITE</label>
                  <input
                    type="number"
                    value={count}
                    onChange={(e) => setCount(e.target.value)}
                    className="component-count-input"
                  />
                </div>
              )}
              <div style={{ marginTop: "clamp(16px, 2vw, 33px)" }}>
                <label className="required-label-style">
                  Delivery <span style={{ color: "red" }}>*</span>
                </label>
                <Datetime
                  value={delivery_date}
                  onChange={(date) => {
                    if (date && date.isValid && date.isValid()) {
                      setDeliveryDate(date.toDate());
                    } else {
                      setDeliveryDate(null);
                    }
                  }}
                  inputProps={{
                    className: "form-input-style",
                    style: {
                      width: "100%",
                      marginTop: "clamp(3px, 0.3vw, 6px)",
                    },
                  }}
                  input={true}
                />
                {formErrors.delivery_date && (
                  <div className="validation-error">
                    {formErrors.delivery_date}
                  </div>
                )}
              </div>
            </div>
          </div>
          <button className="add-procedure-button" onClick={addProcedure}>
            <IoMdAdd /> Add Another Procedure
          </button>
        </div>
        <ToothSelectorModal
          show={showToothSelectorModal}
          handleClose={() => setShowToothSelectorModal(false)}
          currentTeeth={procedures[currentProcedureIndex]?.teethCount || ""}
          onSave={handleSaveTeeth}
          procedureIndex={currentProcedureIndex}
        />
        <ProcedureSelectorModal
          show={showProcedureSelectorModal}
          handleClose={() => setShowProcedureSelectorModal(false)}
          onSelect={handleProcedureSelect}
          allProcedures={procedureOptions.filter(opt => opt.value !== '')}
          procedureIndex={currentProcedureIndex}
        />
      </div>
    </>
  );
};

export default WorkRequested;